import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IGetStrategyResponse,
  IStrategy,
} from 'src/services/dashboard/strategies/strategies.interface';
import {
  createStrategy,
  getStrategies,
} from 'src/services/dashboard/strategies/strategies.service';
import { IStrategyState } from './Strategies.interface';

/**
 * The initial state for the Strategy slice.
 *
 * @constant
 * @type {IStrategyState}
 */
const initialState: IStrategyState = {
  loading: false,
  filter: {
    areaName: null,
    channelId: null,
    indicatorName: null,
    periodId: null,
    quarterId: null,
  },
  strategy: {
    strategyName: '',
    description: '',
    detailLogical: '',
    parameters: [],
    type: 'goal',
  },
  strategies: [],
};

/**
 * A slice of the Redux store responsible for managing strategy data related to KPIs.
 *
 * This slice contains actions and reducers to control the loading state,
 * manage strategies, and handle API responses for creating and retrieving strategies.
 *
 * @constant
 * @type {Slice<IStrategyState>}
 */
const StrategySlice = createSlice({
  name: 'KPIForm',
  initialState,
  reducers: {
    /**
     * Sets the filter criteria based on the provided payload.
     *
     * @param {IStrategyState} state - The current state of the Strategy slice.
     * @param {PayloadAction<IStrategyState['filter']>} action - The action containing the new filter criteria.
     */
    setFilter: (state, action: PayloadAction<IStrategyState['filter']>) => {
      state.filter = action.payload;
    },
    /**
     * Sets the strategy data based on the provided payload.
     *
     * @param {IStrategyState} state - The current state of the Strategy slice.
     * @param {PayloadAction<IStrategyState['strategy']>} action - The action containing the new strategy data.
     */
    setStrategy: (state, action: PayloadAction<IStrategyState['strategy']>) => {
      state.strategy = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        createStrategy.fulfilled,
        (state, action: PayloadAction<IStrategy[]>) => {
          state.loading = false;
          state.strategy = initialState.strategy;
        },
      )
      .addCase(
        getStrategies.fulfilled,
        (state, action: PayloadAction<IGetStrategyResponse[]>) => {
          state.loading = false;
          state.strategies = action.payload;
        },
      );
  },
});

/**
 * Action creators generated from the Strategy slice.
 *
 * @constant
 * @type {{ setFilter: (filter: IStrategyState['filter']) => PayloadAction<IStrategyState['filter']>, setStrategy: (strategy: IStrategyState['strategy']) => PayloadAction<IStrategyState['strategy']> }}
 */
export const { setFilter, setStrategy } = StrategySlice.actions;

/**
 * The reducer for the Strategy slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<IStrategyState>}
 */
export default StrategySlice.reducer;
